import { useEffect, useMemo, useState } from "react";
import { Package, TrendingDown, Layers, Trophy, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";

// Default sort key + direction. We sort newest-first by default per the
// operator's brief ("by default the latest consumed items").
const DEFAULT_SORT = { key: "created_at", dir: "desc" };

// Each column the user can sort by. `accessor` lets us drive both the
// header click + the sort comparator from one place. The qty column is
// the headline one — "lowest to highest" / "highest to lowest" is what
// the operator explicitly asked to surface.
const COLUMNS = [
  { key: "created_at", label: "When",     align: "left",  width: "min-w-[8rem]" },
  { key: "item_name",  label: "Item",     align: "left",  width: "" },
  { key: "category",   label: "Category", align: "left",  width: "min-w-[7rem]" },
  { key: "qty",        label: "Qty used", align: "right", width: "min-w-[6rem]" },
  { key: "reason",     label: "Reason",   align: "left",  width: "min-w-[7rem]" },
  { key: "created_by_name", label: "By",  align: "left",  width: "min-w-[7rem]" },
];

export default function InventoryConsumption({ yacht }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [catFilter, setCatFilter] = useState("");

  useEffect(() => {
    if (!yacht?.id) return;
    setLoading(true);
    // Fire the two requests in parallel: the raw movement list (table) and
    // the existing summary aggregate (top-consumed / totals strip).
    Promise.all([
      api.get(`/inventory/consumption-movements?yacht_id=${yacht.id}`).then((r) => r.data),
      api.get(`/inventory/consumption?yacht_id=${yacht.id}&months=12`).then((r) => r.data),
    ])
      .then(([list, agg]) => {
        setRows(list);
        setSummary(agg.summary);
      })
      .catch((e) => {
        toast.error(fmtErr(e));
        setRows([]);
        setSummary(null);
      })
      .finally(() => setLoading(false));
  }, [yacht?.id]);

  const categories = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.category) s.add(r.category); });
    return [...s].sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    let out = catFilter ? rows.filter((r) => r.category === catFilter) : rows.slice();
    const { key, dir } = sort;
    out.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      // qty is numeric, dates compare correctly as ISO strings, the rest
      // are strings.
      let cmp;
      if (key === "qty") cmp = (av || 0) - (bv || 0);
      else cmp = String(av || "").localeCompare(String(bv || ""));
      return dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, sort, catFilter]);

  const onSort = (key) => {
    setSort((s) => {
      if (s.key !== key) {
        // First click on a new column → desc for numeric/date, asc for text.
        const dir = key === "qty" || key === "created_at" ? "desc" : "asc";
        return { key, dir };
      }
      // Same column → flip direction.
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  };

  if (!yacht?.id) return null;

  return (
    <div className="mt-8 space-y-6" data-testid="consumption-view">
      {/* Summary strip — kept from the previous view because operators
          asked for the numbers, not the chart. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard
          icon={<Trophy className="h-4 w-4 text-amber-600" />}
          label="Top consumed this month"
          testid="summary-top-item"
          value={
            summary?.top_item
              ? (
                <span>
                  <span className="font-medium">{summary.top_item.name}</span>
                  <span className="text-zinc-500"> · {summary.top_item.consumed} used</span>
                </span>
              )
              : <span className="text-zinc-400">No consumption yet</span>
          }
        />
        <SummaryCard
          icon={<TrendingDown className="h-4 w-4 text-red-600" />}
          label="Total units (last 12m)"
          testid="summary-total"
          value={<span className="font-mono font-semibold">{summary?.total_units ?? 0}</span>}
        />
        <SummaryCard
          icon={<Layers className="h-4 w-4 text-sky-600" />}
          label="Categories tracked"
          testid="summary-cats"
          value={<span className="font-mono font-semibold">{summary?.categories_tracked ?? 0}</span>}
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="h-9 rounded-md border border-input px-3 text-sm"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          data-testid="consumption-cat-filter"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="text-xs text-zinc-500 ml-auto">
          {visibleRows.length} movement{visibleRows.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Sortable list */}
      <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="consumption-list">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="h-6 w-6 text-zinc-300 mx-auto" />
            <div className="mt-2 text-sm text-zinc-500">No consumption recorded yet.</div>
            <div className="mt-1 text-[11px] text-zinc-400">
              Use the wrench icon on any inventory row to log a used / broken / lost movement.
            </div>
          </div>
        ) : (
          <>
          {/* MOBILE + TABLET — stacked movement cards (the table only has
              room on large screens; anything narrower was clipping the
              right-hand columns) */}
          <ul className="divide-y divide-zinc-100 lg:hidden" data-testid="consumption-cards">
            {visibleRows.map((r) => (
              <li key={r.id} className="p-3" data-testid={`consumption-card-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{r.item_name}</div>
                    {r.part_number && <div className="text-[11px] text-zinc-400">{r.part_number}</div>}
                    <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-zinc-500">
                      <span>{r.category}</span>
                      {r.reason && <span>· {r.reason}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-sm">−{r.qty}</div>
                    <div className="text-[10px] text-zinc-400 whitespace-nowrap">{fmtWhen(r.created_at)}</div>
                  </div>
                </div>
                {r.created_by_name && <div className="mt-1 text-[10px] text-zinc-400">by {r.created_by_name}</div>}
              </li>
            ))}
          </ul>
          {/* DESKTOP (lg+) — sortable table; scrolls horizontally as a last
              resort instead of clipping the right-hand columns */}
          <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                {COLUMNS.map((c) => (
                  <SortHeader
                    key={c.key}
                    column={c}
                    sort={sort}
                    onClick={() => onSort(c.key)}
                  />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {visibleRows.map((r) => (
                <tr key={r.id} data-testid={`consumption-row-${r.id}`}>
                  <td className="py-2 px-3 text-xs text-zinc-600 whitespace-nowrap">
                    {fmtWhen(r.created_at)}
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-medium truncate">{r.item_name}</div>
                    {r.part_number && (
                      <div className="text-[11px] text-zinc-400">{r.part_number}</div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-zinc-600">{r.category}</td>
                  <td className="py-2 px-3 text-right font-mono">{r.qty}</td>
                  <td className="py-2 px-3 text-zinc-600">{r.reason || "—"}</td>
                  <td className="py-2 px-3 text-zinc-600 text-xs">{r.created_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

function SortHeader({ column, sort, onClick }) {
  const active = sort.key === column.key;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2 font-medium ${column.width}`} style={{ textAlign: column.align }}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-zinc-900 ${active ? "text-zinc-900" : ""}`}
        data-testid={`sort-${column.key}`}
      >
        {column.label}
        <Icon className="h-3 w-3 opacity-60" />
      </button>
    </th>
  );
}

function SummaryCard({ icon, label, value, testid }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-[14px] text-zinc-800">{value}</div>
    </div>
  );
}

// "2026-05-26T07:55:18Z" → "26 May, 07:55". Keeps the timestamp compact
// enough to fit alongside the other columns on a phone landscape.
function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const day = d.getDate();
    const mon = d.toLocaleString("en", { month: "short" });
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${day} ${mon}, ${hh}:${mm}`;
  } catch { return iso; }
}
